
import { DateTime, Interval } from 'luxon';
import { inflate } from 'pako';


class TornadoCollectionTree {
    constructor(tors) {
        this.left_child = null;
        this.right_child = null;
        this.leaf = null;

        this.interval = null;
        if (tors.length > 0) {
            // Add a second to the interval end to make sure that the tornado on the interval end gets counted
            let intv_start = tors[0]['datetime'];
            let intv_end = tors[tors.length - 1]['datetime'].plus({'seconds': 1});

            this.interval = Interval.fromDateTimes(intv_start, intv_end);
        }

        if (tors.length <= 20) {
            this.leaf = tors;
        }
        else {
            const split = Math.floor(tors.length / 2);
            this.left_child = new TornadoCollectionTree(tors.slice(0, split));
            this.right_child = new TornadoCollectionTree(tors.slice(split));
        }
    }

    isLeaf() {
        return this.leaf !== null;
    }

    getNTornadoes(interval, partitioner) {
        const partition_func = partitioner === undefined ? tl => {return {'count': tl};} : partitioner.partition.bind(partitioner);

        let partitions = partition_func(this.search(interval, false).filter(tor => tor['geojson']['geometry']['type'] != 'Polygon'));
        return Object.fromEntries(Object.entries(partitions).map(([k, v]) => [k, v.length]));
    }

    getGeoJSON(interval, partitioner) {
        const partition_func = partitioner === undefined ? tl => tl : partitioner.partition.bind(partitioner);

        let partitions = partition_func(this.search(interval, false));
        return Object.fromEntries(Object.entries(partitions).map(([k, v]) => [k, v.map(tor => tor['geojson'])]));
    }

    getPropertyValues(property, interval, partitioner) {
        const dummy_partition_func = tl => {
            let part = {};
            part[property] = tl;
            return part;
        }

        const partition_func = partitioner === undefined ? dummy_partition_func: partitioner.partition.bind(partitioner);
        let partitions = partition_func(this.search(interval, false).filter(tor => tor['geojson']['geometry']['type'] != 'Polygon'));

        return Object.fromEntries(Object.entries(partitions).map(([k, v]) => [k, v.map(tor => tor['geojson']['properties'][property])]));
    }

    search(interval, _debug) {
        if (this.interval === null || !interval.overlaps(this.interval)) {
            return [];
        }
        else {
            if (this.isLeaf()) {
                if (interval.engulfs(this.interval)) {
                    return this.leaf;
                }
                else {
                    return this.leaf.filter(tor => interval.contains(tor['datetime']));
                }
            }
            else {
                let left = this.left_child.search(interval, _debug);
                let right = this.right_child.search(interval, _debug);
                return left.concat(right);
            }
        }
    }
}


class TornadoCollection {
    constructor(tors) {
        this._tors = tors;
        this.tor_tree = new TornadoCollectionTree(tors);
    }

    getNTornadoes(subintvs, partitioner) {
        // Get number of tornadoes over a bunch of temporal intervals
        const n_tors = subintvs.map(sintv => this.tor_tree.getNTornadoes(sintv, partitioner));
        return n_tors;
    }

    getGeoJSON(interval, partitioner) {
        // Put all tornadoes whose start time is in the interval into a geojson object
        var tors = Object.fromEntries(Object.entries(this.tor_tree.getGeoJSON(interval, partitioner))
                                            .map(([k, part]) => [k, {'type': 'FeatureCollection', 'features': part}]));

        return tors;
    }

    getPropertyValues(property, interval, partitioner) {
        return this.tor_tree.getPropertyValues(property, interval, partitioner);
    }

    applyFilters(filters) {
        let tors = this._tors;
        Object.values(filters).map(filter => filter['func']).forEach(filter => {
            tors = tors.filter(tor => filter(tor));
        })

        return new TornadoCollection(tors);
    }

    static fromServer(url) {
        const gj_feature = feat => ({'datetime': DateTime.fromISO(feat['properties']['datetime-start'], {'zone': 'UTC'}), 'geojson': feat});
        return fetch(url)
            .then(resp => resp.blob())
            .then(blob => blob.arrayBuffer())
            .then(bufr => new Uint8Array(bufr))
            .then(arry => inflate(arry, {'to': 'string'}))
            .then(resp => JSON.parse(resp))
            .then(tor_gj => tor_gj['features'].map(gj_feature))
            .then(tor_list => new TornadoCollection(tor_list))
    }
}

export default TornadoCollection;
